if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();

        this.onNavigationClick = this.onNavigationClick.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onZoomPointerMove = this.onZoomPointerMove.bind(this);
        this.onZoomPointerEnter = this.onZoomPointerEnter.bind(this);
        this.onZoomPointerLeave = this.onZoomPointerLeave.bind(this);
      }

      connectedCallback() {
        super.connectedCallback();
        this.prevButton = this.querySelector('#ModalPrev-' + this.dataset.section);
        this.nextButton = this.querySelector('#ModalNext-' + this.dataset.section);

        this.prevButton?.addEventListener('click', this.onNavigationClick);
        this.nextButton?.addEventListener('click', this.onNavigationClick);
        this.prevButton?.addEventListener('pointerup', (event) => event.stopPropagation());
        this.nextButton?.addEventListener('pointerup', (event) => event.stopPropagation());
      }

      show(opener) {
        super.show(opener);
        this.refreshSlides();
        this.currentSlideIndex = this.getActiveIndex();
        this.showActiveMedia();
        this.bindZoomEvents();
        document.addEventListener('keydown', this.onKeydown);
      }

      hide() {
        document.removeEventListener('keydown', this.onKeydown);
        this.unbindZoomEvents();
        this.currentSlide = null;
        this.currentSlideIndex = null;
        super.hide();
        document.body.classList.remove(
          'overflow-hidden',
          'overflow-hidden-mobile',
          'overflow-hidden-tablet',
          'overflow-hidden-desktop'
        );
        document.body.style.removeProperty('overflow');
        document.documentElement.style.removeProperty('overflow');
      }

      refreshSlides() {
        this.slides = Array.from(this.querySelectorAll('.product-media-modal__content > [data-media-id]'));
      }

      getZoomableImages() {
        return (this.slides || [])
          .map((slide) => (slide.matches?.('img[data-media-id]') ? slide : slide.querySelector('img[data-media-id]')))
          .filter(Boolean);
      }

      bindZoomEvents() {
        this.getZoomableImages().forEach((image) => {
          if (image.dataset.zoomBound === 'true') return;

          image.dataset.zoomBound = 'true';
          image.addEventListener('pointerenter', this.onZoomPointerEnter);
          image.addEventListener('pointermove', this.onZoomPointerMove);
          image.addEventListener('pointerleave', this.onZoomPointerLeave);
        });
      }

      unbindZoomEvents() {
        this.getZoomableImages().forEach((image) => {
          if (image.dataset.zoomBound !== 'true') return;

          image.removeEventListener('pointerenter', this.onZoomPointerEnter);
          image.removeEventListener('pointermove', this.onZoomPointerMove);
          image.removeEventListener('pointerleave', this.onZoomPointerLeave);
          delete image.dataset.zoomBound;
          this.resetZoom(image);
        });
      }

      resetZoom(image) {
        if (!image) return;

        image.style.removeProperty('transition');
        image.style.removeProperty('transform-origin');
        image.style.removeProperty('transform');
        image.style.cursor = 'zoom-in';
      }

      onZoomPointerEnter(event) {
        this.updateZoom(event.currentTarget, event);
      }

      onZoomPointerMove(event) {
        this.updateZoom(event.currentTarget, event);
      }

      onZoomPointerLeave(event) {
        this.resetZoom(event.currentTarget);
      }

      updateZoom(image, event) {
        if (!image || event.pointerType === 'touch') return;

        const rect = image.getBoundingClientRect();
        if (!rect.width || !rect.height) return;

        const x = ((event.clientX - rect.left) / rect.width) * 100;
        const y = ((event.clientY - rect.top) / rect.height) * 100;

        image.style.transition = 'none';
        image.style.transformOrigin = `${Math.min(100, Math.max(0, x))}% ${Math.min(100, Math.max(0, y))}%`;
        image.style.transform = 'scale(2)';
        image.style.cursor = 'zoom-out';
      }

      getActiveIndex() {
        if (!this.slides?.length) return -1;

        // Prefer the media the user actually clicked to open the modal; fall back
        // to the slide the modal is already on (keyboard/arrow navigation).
        const activeId =
          this.openedBy?.getAttribute('data-media-id') || this.currentSlide?.getAttribute('data-media-id');
        if (!activeId) return 0;

        const matchingIndex = this.slides.findIndex((slide) => slide.getAttribute('data-media-id') === activeId);
        return matchingIndex >= 0 ? matchingIndex : 0;
      }

      showActiveMedia() {
        this.refreshSlides();
        if (!this.slides.length) return;

        const activeIndex = this.getActiveIndex();
        const activeSlide = this.slides[activeIndex];
        if (!activeSlide) return;

        this.currentSlideIndex = activeIndex;
        this.currentSlide = activeSlide;

        this.slides.forEach((slide) => slide.classList.remove('active'));
        activeSlide.classList.add('active');

        activeSlide.scrollIntoView({
          behavior: 'auto',
          block: 'nearest',
          inline: 'start',
        });

        this.loadSlideMedia(activeSlide);

        this.getZoomableImages().forEach((image) => this.resetZoom(image));
        this.updateNavigationState();
      }

      updateNavigationState() {
        const hasMultipleSlides = (this.slides?.length || 0) > 1;
        const currentIndex = Math.max(this.currentSlideIndex ?? 0, 0);
        const lastIndex = Math.max((this.slides?.length || 1) - 1, 0);

        if (this.prevButton) {
          this.prevButton.hidden = !hasMultipleSlides;
          this.prevButton.disabled = !hasMultipleSlides || currentIndex <= 0;
        }

        if (this.nextButton) {
          this.nextButton.hidden = !hasMultipleSlides;
          this.nextButton.disabled = !hasMultipleSlides || currentIndex >= lastIndex;
        }
      }

      onNavigationClick(event) {
        event.preventDefault();
        this.navigate(event.currentTarget === this.prevButton ? -1 : 1);
      }

      onKeydown(event) {
        if (event.code === 'ArrowLeft') {
          event.preventDefault();
          this.navigate(-1);
        }

        if (event.code === 'ArrowRight') {
          event.preventDefault();
          this.navigate(1);
        }
      }

      navigate(direction) {
        this.refreshSlides();
        if (!this.slides.length) return;

        const currentIndex = Math.max(this.currentSlideIndex ?? this.getActiveIndex(), 0);
        const nextIndex = currentIndex + direction;
        if (nextIndex < 0 || nextIndex >= this.slides.length) {
          this.updateNavigationState();
          return;
        }

        const nextSlide = this.slides[nextIndex];
        if (!nextSlide) return;

        this.currentSlideIndex = nextIndex;
        this.currentSlide = nextSlide;
        this.slides.forEach((slide) => slide.classList.remove('active'));
        nextSlide.classList.add('active');
        nextSlide.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'start',
        });

        this.loadSlideMedia(nextSlide);

        this.getZoomableImages().forEach((image) => this.resetZoom(image));
        this.updateNavigationState();
      }

      loadSlideMedia(slide) {
        const deferredMedia = slide.matches?.('deferred-media, product-model')
          ? slide
          : slide.querySelector?.('deferred-media, product-model');

        if (deferredMedia?.loadContent && !deferredMedia.hasAttribute('loaded')) {
          deferredMedia.loadContent(false);
        }
      }
    }
  );
}
